import {
  ComponentProps,
  FC,
  Key,
  MouseEvent,
  PropsWithChildren,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react'
import { useRouter } from 'next/router'
import {
  ActionMenu,
  Icon,
  MenuItem,
  Overlay,
  Switch,
  Textfield,
  Theme,
  Toast
} from 'ui'
import Skeleton from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'
import cn from 'classnames'
// @ts-ignore
import Avatar from '@react/react-spectrum/Avatar'
import styles from '@styles/components/ProjectCard.module.scss'
import {
  Maybe,
  Project,
  User,
  useDuplicateProjectMutation,
  useGetUserLazyQuery,
  useUpdateProjectMutation
} from '@store/graphql/__generated__/schema'
import { useAuth } from '@hooks/useAuth'
import Reactions, { ReactionType } from '@components/reactions/Reactions'
import ImgPlaceHolderIcon from '/public/img_placeholder.svg'
import ClockPendingIcon from '/public/s2_icon_clock_pending.svg'
import UserIcon from '/public/s2_icon_user.svg'
import DeleteIcon from '/public/s2_icon_trash.svg'
import DrawIcon from '/public/s2_icon_draw.svg'
import DuplicateIcon from '/public/s2_icon_duplicate.svg'
import StarIcon from '/public/s2_icon_star.svg'
import ArchiveIcon from '/public/s2_icon_archive.svg'
import PreviewIcon from '/public/s2_icon_visibility.svg'
import { getProjectCardHeightFromDocumentFrameSize } from './ProjectCardSize'
import { useFlags } from 'launchdarkly-react-client-sdk'
import ProjectDeleteDialog from './ProjectDeleteDialog'
import { useHistoryActions } from '@hooks/useHistory'
import { useProjectActions } from '@hooks/useProject'
import { ReportButton } from '@components/reportProject/ReportButton'
import SharedBadge from './SharedBadge'
import useLocalizedRelativeDate from '@concerns/i18n/components/useLocalizedRelativeDate'
import { useTranslations } from 'use-intl'
import { LocalStorageKey } from '@constants/localStorage'

export interface ProjectCardProps {
  createdAt: Project['insertedAt']
  name: Project['name']
  imageUrl: Project['imageUrl']
  uuid: Project['uuid']
  public: Project['public']
  featured: Project['featured']
  documentFrameSize: Project['documentFrameSize']
  editingEnabled?: boolean
  totalReactions: Project['totalReactions']
  userReactedWith: Project['userReactedWith']
  uniqueReactionTypes: Project['uniqueReactionTypes']
  adminEditingEnabled?: boolean
  showShareBtn?: boolean
  showReactions?: boolean
  projectOwnerUuid: User['uuid']
  getCachedCardHeight?: (uuid: string, documentFrameSize: any) => number
  showSharedBadge?: boolean
  precomputedDate?: string
}

type ProjectCardActionMenuProps = Omit<
  ComponentProps<typeof ActionMenu>,
  'className'
> & { showProjectCardOverlay: boolean }

const ProjectCardActionMenu = memo(
  ({
    children,
    showProjectCardOverlay,
    ...props
  }: PropsWithChildren<ProjectCardActionMenuProps>) => {
    return (
      <ActionMenu
        {...props}
        className={cn(
          styles['action-menu'],
          showProjectCardOverlay && styles['action-menu__hover']
        )}
      >
        {children}
      </ActionMenu>
    )
  }
)

const ProjectCard: FC<ProjectCardProps> = ({
  createdAt,
  name,
  imageUrl,
  uuid,
  public: projectPublic,
  featured,
  documentFrameSize,
  editingEnabled,
  totalReactions,
  userReactedWith,
  uniqueReactionTypes,
  adminEditingEnabled,
  showShareBtn,
  showReactions,
  projectOwnerUuid,
  getCachedCardHeight,
  showSharedBadge = false,
  precomputedDate
}) => {
  const flags = useFlags()
  const router = useRouter()
  const { localUser } = useAuth()
  const { setShowHistoryDialog } = useHistoryActions()
  const {
    setProjectUuid,
    setIsPublic,
    setIsFeatured,
    setCreatedAt,
    setName,
    setOwner
  } = useProjectActions()
  const t = useTranslations()

  const menuItems = [
    ...(projectPublic ? [{ key: 'preview-link', icon: <PreviewIcon /> }] : []),
    { key: 'duplicate', icon: <DuplicateIcon /> },
    { key: 'archive', icon: <DeleteIcon /> },
    { key: 'rename', icon: <DrawIcon /> }
  ]

  const [updateProject] = useUpdateProjectMutation()
  const [duplicateProject] = useDuplicateProjectMutation()
  const [getUser, { data }] = useGetUserLazyQuery()
  const projectOwner = data?.user || undefined

  const [alreadyFetchedProjectOwner, setAlreadyFetchedProjectOwner] =
    useState<boolean>(false)

  const [nameInputValue, setNameInputValue] = useState(name)
  const [showNameInput, setShowNameInput] = useState<boolean>(false)

  const [imageLoaded, setImageLoaded] = useState<boolean>(false)
  const [errorLoadingImage, setErrorLoadingImage] = useState<boolean>(false)

  const [isHovered, setIsHovered] = useState<boolean>(false)
  const [
    shouldLoadDynamicComponentOnHover,
    setShouldLoadDynamicComponentOnHover
  ] = useState<boolean>(false)

  const [shouldShowOverlay, setShouldShowOverlay] = useState<boolean>(false)
  const [shouldShowDialog, setShouldShowDialog] = useState<boolean>(false)

  const [showProjectActionMenu, setShowProjectActionMenu] =
    useState<boolean>(false)
  const [showProjectCardOverlay, setShowProjectCardOverlay] =
    useState<boolean>(false)
  const [showDeleteWarningDialog, setShowDeleteWarningDialog] = useState(false)

  const projectStudioUrl: string = `/studio/${uuid}`
  const currentUserIsProjectOwner: boolean =
    !!localUser && localUser?.uuid === projectOwnerUuid
  const showReportButton: boolean =
    flags['base-tf-ui-report-abuse'] &&
    !!localUser &&
    !currentUserIsProjectOwner &&
    !!projectOwner?.adobeUserId

  const projectCreatedDate =
    precomputedDate || useLocalizedRelativeDate(createdAt)

  const fetchProjectCreatorData = useCallback(() => {
    if (alreadyFetchedProjectOwner) return

    getUser({
      fetchPolicy: 'cache-and-network',
      onCompleted: () => setAlreadyFetchedProjectOwner(true),
      onError: () => setAlreadyFetchedProjectOwner(false),
      variables: { uuid: projectOwnerUuid! }
    })
  }, [projectOwnerUuid, alreadyFetchedProjectOwner])

  // TODO (cam): this seems confusing as heck; the `getCachedCardHeight/2` can prpobably be refactored to support both of these cases
  const normalizedFrameDimensionHeight = useMemo(
    () => ({
      height:
        typeof getCachedCardHeight === 'function'
          ? getCachedCardHeight(uuid as string, documentFrameSize)
          : getProjectCardHeightFromDocumentFrameSize(
              documentFrameSize,
              uuid as string
            )
    }),
    [getCachedCardHeight, documentFrameSize, uuid]
  )

  // TODO (cam): we should consolidate the `updateProject` mutations into a single mutation both here and in the backend

  // TODO (cam): we could probably handle the update async and optimistically return a response from this callback
  const toggleProjectPublic = useCallback(() => {
    return updateProject({
      variables: { uuid: uuid as string, public: !projectPublic }
    })
  }, [uuid, projectPublic])

  // TODO (cam): we could probably handle the update async and optimistically return a response from this callback
  const toggleProjectFeatured = useCallback(() => {
    return updateProject({
      variables: {
        uuid: uuid as string,
        featured: !featured
      }
    })
  }, [uuid, featured])

  const handleMenuItemSelection = useCallback(
    (key: Key) => {
      switch (key) {
        case 'duplicate':
          return duplicateProject({ variables: { uuid: uuid as string } })
        case 'archive':
          return openDeleteWarningDialog()
        case 'rename':
          return openNameInput()
        case 'preview-link':
          // ensure that when the history dialog is closed, the user is redirected back to /my-files
          // This is a temporary solution until we implement the new user profile page
          localStorage.setItem(
            LocalStorageKey.historyRedirectUrl,
            '/my-projects'
          )
          return openPreviewTab(`/discover/${uuid}`)
        default:
          return null
      }
    },
    [uuid]
  )

  const openPreviewTab = (url: string) => {
    const newWindow = window.open(url, '_blank', 'noopener,noreferrer')
    if (newWindow) newWindow.opener = null
  }

  const openNameInput = useCallback(() => setShowNameInput(true), [])
  const closeNameInput = useCallback(() => setShowNameInput(false), [])

  const handleNameInput = useCallback(e => {
    const value = e.currentTarget.value
    setNameInputValue(value)
  }, [])

  const handleNameInputChange = useCallback(
    e => {
      const value = e.currentTarget.value
      if (value !== name && value !== '') {
        updateProject({
          variables: {
            uuid: uuid as string,
            name: value
          }
        })
      }

      setShowNameInput(false)
    },
    [updateProject, name]
  )

  const openDeleteWarningDialog = useCallback(
    () => setShowDeleteWarningDialog(true),
    []
  )

  const navigateToUserPage = useCallback(
    () => router.push(`/profile/${projectOwnerUuid}`),
    []
  )

  const toggleProjectActionMenu = useCallback(
    event => {
      event.stopPropagation()
      event.preventDefault()
      setShowProjectActionMenu(!showProjectActionMenu)
    },
    [showProjectActionMenu]
  )

  const preloadOverlayComponent = useCallback(() => {
    setShouldShowOverlay(true)
    setShouldLoadDynamicComponentOnHover(true)
  }, [])

  const preloadDialogComponent = useCallback(
    () => setShouldShowDialog(true),
    []
  )

  const handleMouseEnter = useCallback(() => setIsHovered(true), [])
  const handleMouseLeave = useCallback(() => setIsHovered(false), [])
  const onImageLoad = useCallback(() => setImageLoaded(true), [])
  const onImageLoadErr = useCallback(() => setErrorLoadingImage(true), [])

  const onOutsideUnderlayClick = useCallback(() => {
    setShowProjectActionMenu(false)
    setShowProjectCardOverlay(false)
  }, [])

  const openHistoryDialog = () => {
    // set the uuid so engine can load and initialize the project
    setProjectUuid(uuid as string)
    name && setName(name)
    setIsPublic(!!projectPublic)
    setIsFeatured(!!featured)
    setCreatedAt(projectCreatedDate)
    projectOwner && setOwner(projectOwner)
    setShowHistoryDialog({
      isOpen: true
    })
  }

  const handleProjectCardClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault()

      // Don't do anything from click events that bubbles from card buttons and menus
      if (event.target !== event.currentTarget) {
        return
      }

      // go to studio page if feature flag is off or if the user is the project creator
      if (currentUserIsProjectOwner || !flags['base-pf-ui-history-slider']) {
        // TODO: this does not properly wait for engine to unmount
        router.push(projectStudioUrl)
      } else {
        openHistoryDialog()
      }
    },
    [projectOwner]
  )

  useEffect(() => {
    let timer: NodeJS.Timeout

    if (isHovered) {
      // delay the hover to prevent accidental loading of the dynamic component while scrolling
      timer = setTimeout(() => {
        setShouldLoadDynamicComponentOnHover(true)
      }, 500)
      setShowProjectCardOverlay(true)
      fetchProjectCreatorData()
    } else {
      setShowProjectCardOverlay(false)
    }
    return () => clearTimeout(timer)
  }, [isHovered])

  return (
    <div data-testid="project-card">
      {/* This is to detect outside clicks. It's needed because we need a way to detect if a user
    has clicked outside of an ActionMenu to close it. Unfortunately, Spectrum does not provide
    an API for this */}
      {showProjectActionMenu && (
        <div
          className={styles['outside-click-underlay']}
          onClick={onOutsideUnderlayClick}
        />
      )}
      <a
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        href={projectStudioUrl}
        onClick={handleProjectCardClick}
        data-testid="project-card-container"
        className={styles['container']}
      >
        <div className={styles['project-card']}>
          <div
            className={cn(
              styles['project-card-overlay'],
              (showProjectCardOverlay || showNameInput) &&
                styles['project-card-overlay__hover']
            )}
          />
          <div
            style={normalizedFrameDimensionHeight}
            className={styles['project-card__img-container']}
          >
            <div
              className={cn(
                styles['project-card__content'],
                (showProjectCardOverlay || showNameInput) &&
                  styles['project-card__content__hover']
              )}
            >
              <div className={styles['project-card__project-title-container']}>
                {projectOwner?.adobeUserAvatarUrl && (
                  <div>
                    <Avatar
                      className={styles['project-card__avatar']}
                      src={projectOwner?.adobeUserAvatarUrl}
                      alt={
                        projectOwner.adobeUserDisplayName
                          ? t('dashboard:projectCard:creator:avatarAlt', {
                              username: projectOwner.adobeUserDisplayName
                            })
                          : t('dashboard:projectCard:creator:avatarAltDefault')
                      }
                    />
                  </div>
                )}

                <div>
                  <div>
                    {!showNameInput ? (
                      <>
                        {imageLoaded || errorLoadingImage ? (
                          <div
                            data-testid="project-name-container"
                            className={styles['project-card__project-title']}
                          >
                            {name}
                          </div>
                        ) : (
                          <Skeleton
                            height={20}
                            width={70}
                            baseColor="#e6e6e6"
                            highlightColor="#f2f2f2bf"
                            style={{ marginBottom: 3 }}
                          />
                        )}
                      </>
                    ) : (
                      <Textfield
                        style={{ pointerEvents: 'all', width: 180 }}
                        autofocus
                        onBlur={closeNameInput}
                        onInput={handleNameInput}
                        onChange={handleNameInputChange}
                        value={nameInputValue}
                      />
                    )}
                  </div>
                  <div className={styles['project-card__display-name']}>
                    {t('dashboard:projectCard:creator', {
                      username:
                        projectOwner?.adobeUserDisplayName ||
                        t('dashboard:projectCard:creatorDefault')
                    })}
                  </div>
                </div>
              </div>
              <div
                className={
                  styles['project-card__shared_badge_created_at_container']
                }
              >
                {showSharedBadge &&
                  projectPublic &&
                  shouldLoadDynamicComponentOnHover && <SharedBadge size="s" />}
                <div className={styles['project-card__created-at']}>
                  <ClockPendingIcon />
                  {projectCreatedDate}
                </div>
              </div>
            </div>

            {(!imageLoaded || errorLoadingImage) && (
              <div className={styles['img-placeholder']}>
                <div className={styles['img-placeholder-content']}>
                  {errorLoadingImage ? (
                    <ImgPlaceHolderIcon />
                  ) : (
                    <div
                      className={styles['img-placeholder-skeleton-container']}
                    >
                      <Skeleton
                        height={normalizedFrameDimensionHeight.height}
                        width="100%"
                        baseColor="#e6e6e6"
                        highlightColor="#f2f2f2bf"
                      />
                    </div>
                  )}
                </div>
              </div>
            )}

            <img
              data-testid={`project-card-img-${name}`}
              className={styles['project-card__img']}
              onLoad={onImageLoad}
              onError={onImageLoadErr}
              src={`${process.env.NEXT_PUBLIC_SERVICE_CORE_STORAGE}${imageUrl}${
                currentUserIsProjectOwner ? `?time=${Date.now()}` : ''
              }`}
              alt={name}
              aria-label={name}
            />
          </div>
        </div>

        {adminEditingEnabled && shouldLoadDynamicComponentOnHover && (
          <div
            className={cn(
              styles['action-menu-container'],
              styles['action-menu-container-left-position']
            )}
          >
            <ProjectCardActionMenu
              onClick={toggleProjectActionMenu}
              showProjectCardOverlay={showProjectCardOverlay}
            >
              <MenuItem>
                <Switch
                  checked={featured}
                  onClick={toggleProjectFeatured}
                  emphasized
                >
                  <Icon slot="icon" className="icon-m">
                    <StarIcon />
                  </Icon>
                  {t('dashboard:projectCard:menu:adminFeature')}
                </Switch>
              </MenuItem>
              <MenuItem
                onMouseEnter={preloadDialogComponent}
                onClick={openDeleteWarningDialog}
              >
                <Icon slot="icon" className="icon-m">
                  <ArchiveIcon />
                </Icon>
                {t('dashboard:projectCard:menu:adminArchive')}
              </MenuItem>
            </ProjectCardActionMenu>
          </div>
        )}

        {showShareBtn &&
          !showNameInput &&
          shouldLoadDynamicComponentOnHover && (
            <div
              className={styles['action-menu-container']}
              onMouseEnter={preloadOverlayComponent}
            >
              <>
                <Theme system="express" color="light">
                  <ProjectCardActionMenu
                    onClick={toggleProjectActionMenu}
                    showProjectCardOverlay={showProjectCardOverlay}
                  >
                    <MenuItem onClick={navigateToUserPage}>
                      <Icon slot="icon" className="icon-m">
                        <UserIcon />
                      </Icon>
                      {t('dashboard:projectCard:menu:moreFromUser')}
                    </MenuItem>
                    {showReportButton && (
                      <ReportButton
                        projectUuid={uuid!}
                        ownerAdobeUserId={projectOwner!.adobeUserId!}
                      />
                    )}
                  </ProjectCardActionMenu>

                  {shouldShowOverlay && (
                    <Overlay
                      trigger={`share-trigger-${uuid}@click`}
                      type="manual"
                    >
                      <Toast
                        variant="positive"
                        className={styles['toast']}
                        timeout={1500}
                      >
                        {t('dashboard:projectCard:linkCopiedMessage')}
                      </Toast>
                    </Overlay>
                  )}
                </Theme>
              </>
            </div>
          )}

        {currentUserIsProjectOwner &&
          editingEnabled &&
          !showNameInput &&
          shouldLoadDynamicComponentOnHover && (
            <div className={styles['action-menu-container']}>
              <ProjectCardActionMenu
                onClick={toggleProjectActionMenu}
                showProjectCardOverlay={showProjectCardOverlay}
              >
                <MenuItem>
                  <Switch
                    checked={Boolean(projectPublic)}
                    onClick={toggleProjectPublic}
                    emphasized
                  >
                    {projectPublic
                      ? t('dashboard:projectCard:menu:shared')
                      : t('dashboard:projectCard:menu:private')}
                  </Switch>
                </MenuItem>
                {menuItems.map((item, i) => (
                  <MenuItem
                    key={i}
                    onMouseEnter={() => {
                      if (item.key === 'archive') {
                        preloadDialogComponent()
                      }
                    }}
                    onClick={() => {
                      handleMenuItemSelection(item.key)
                    }}
                  >
                    <Icon slot="icon" className="icon-m">
                      {item.icon}
                    </Icon>
                    {t(`dashboard:projectCard:menu:${item.key}`)}
                  </MenuItem>
                ))}
              </ProjectCardActionMenu>
            </div>
          )}

        {shouldShowDialog && (
          <ProjectDeleteDialog
            show={showDeleteWarningDialog}
            setShow={setShowDeleteWarningDialog}
            projectUuid={uuid!}
          />
        )}

        <div>
          {showReactions && localUser && (
            <div className={styles['reactions-container']}>
              <Reactions
                projectUuid={uuid as string}
                totalReactions={totalReactions || 0}
                userUuid={localUser?.uuid || null}
                userReactedWith={userReactedWith as Maybe<string>}
                uniqueReactionTypes={uniqueReactionTypes as ReactionType[]}
              />
            </div>
          )}
        </div>
      </a>
    </div>
  )
}

export default memo(
  ProjectCard,
  (prevProps, nextProps) =>
    prevProps.uuid === nextProps.uuid &&
    prevProps.name === nextProps.name &&
    prevProps.showShareBtn === nextProps.showShareBtn &&
    prevProps.featured === nextProps.featured &&
    prevProps.public === nextProps.public &&
    prevProps.documentFrameSize === nextProps.documentFrameSize &&
    prevProps.userReactedWith === nextProps.userReactedWith &&
    prevProps.showReactions === nextProps.showReactions &&
    prevProps.totalReactions === nextProps.totalReactions &&
    prevProps.getCachedCardHeight === nextProps.getCachedCardHeight &&
    prevProps.precomputedDate === nextProps.precomputedDate &&
    (prevProps.uniqueReactionTypes || []) ===
      (nextProps.uniqueReactionTypes || [])
)
